import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Music, Palette, Lightbulb, Rocket, Loader2, ArrowLeft, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { studioAPI } from '../lib/api';
import GettingStarted from '../components/guidance/GettingStarted';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const GENRES = ['hip-hop', 'r&b', 'pop', 'rock', 'electronic', 'indie', 'jazz', 'afrobeats', 'latin', 'country', 'soul'];
const MOODS = ['energetic', 'melancholic', 'dark', 'uplifting', 'chill', 'aggressive', 'romantic', 'nostalgic', 'ethereal'];

const parseStrategyPayload = (strategyText) => {
  if (!strategyText) return null;

  try {
    let cleaned = strategyText.trim();
    if (cleaned.startsWith('```json')) cleaned = cleaned.replace(/^```json\s*/, '').replace(/```$/, '').trim();
    else if (cleaned.startsWith('```')) cleaned = cleaned.replace(/^```\s*/, '').replace(/```$/, '').trim();
    return JSON.parse(cleaned);
  } catch {
    return null;
  }
};

const buildLaunchBundlePrefill = (strategyText, fallbackTitle) => {
  const parsed = parseStrategyPayload(strategyText);
  if (!parsed) return null;

  const eventPlan = parsed.event_plan || {};
  const eventDate = new Date();
  const offsetDays = Math.min(60, Math.max(7, Number(eventPlan.event_date_offset_days) || 21));
  eventDate.setDate(eventDate.getDate() + offsetDays);

  const tiers = Array.isArray(parsed.tiers)
    ? parsed.tiers.map((tier, index) => ({
        name: tier.name || `Tier ${index + 1}`,
        amount: Number(tier.amount) || 10,
        perks: Array.isArray(tier.perks) ? tier.perks.join(' • ') : tier.perks || '',
        limit: Number(tier.limit) || 0,
      }))
    : [];

  const ticketTiers = Array.isArray(eventPlan.ticket_tiers) && eventPlan.ticket_tiers.length > 0
    ? eventPlan.ticket_tiers.map((tier, index) => ({
        name: tier.name || `Ticket ${index + 1}`,
        price: Number(tier.price) || 25,
        quantity: Number(tier.quantity) || 100,
      }))
    : [
        { name: 'General Admission', price: 25, quantity: 150 },
        { name: 'VIP', price: 75, quantity: 40 },
      ];

  return {
    title: parsed.project_title || fallbackTitle || 'Untitled launch',
    description: parsed.project_description || '',
    genre: parsed.genre || 'music',
    funding_goal: String(parsed.funding_goal || ''),
    deadline_days: String(parsed.deadline_days || 30),
    tiers,
    create_event: true,
    event_location: eventPlan.location || '',
    event_venue: eventPlan.venue || '',
    event_date: eventDate.toISOString().slice(0, 10),
    event_time: eventPlan.event_time || '20:00',
    ticket_tiers: ticketTiers,
  };
};

export default function StudioPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('lyrics');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [myItems, setMyItems] = useState([]);
  const [funnelResult, setFunnelResult] = useState(null);

  // Form state
  const [genre, setGenre] = useState('hip-hop');
  const [mood, setMood] = useState('energetic');
  const [topic, setTopic] = useState('');
  const [styleRef, setStyleRef] = useState('');
  const [artTitle, setArtTitle] = useState('');
  const [artStyle, setArtStyle] = useState('');

  // Funnel state
  const [funnelTitle, setFunnelTitle] = useState('');
  const [funnelDesc, setFunnelDesc] = useState('');
  const [funnelLoading, setFunnelLoading] = useState(false);
  const parsedFunnel = parseStrategyPayload(funnelResult?.strategy);
  const launchBundlePrefill = buildLaunchBundlePrefill(funnelResult?.strategy, funnelTitle);

  useEffect(() => {
    studioAPI.myItems().then(r => setMyItems(r.data)).catch(() => {});
  }, [result]);

  const handleGenerate = async (type) => {
    setLoading(true); setResult(null);
    try {
      let res;
      if (type === 'lyrics') {
        res = await studioAPI.generateLyrics({ genre, mood, topic, style_reference: styleRef });
      } else if (type === 'concept') {
        res = await studioAPI.generateConcept({ genre, mood, topic, style_reference: styleRef });
      } else if (type === 'album_art') {
        res = await studioAPI.generateAlbumArt({ title: artTitle, genre, mood, style: artStyle });
      }
      setResult(res.data);
      toast.success(`${type} generated!`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Generation failed'); }
    finally { setLoading(false); }
  };

  const handleFunnel = async () => {
    if (!funnelTitle) { toast.error('Add a title'); return; }
    setFunnelLoading(true); setFunnelResult(null);
    try {
      const res = await studioAPI.fundingFunnel({ content_type: 'song', title: funnelTitle, description: funnelDesc });
      setFunnelResult(res.data);
      toast.success('Funding strategy generated!');
    } catch (e) { toast.error('Failed to generate strategy'); }
    finally { setFunnelLoading(false); }
  };

  if (!user) return <div className="min-h-screen bg-zinc-50 flex items-center justify-center"><p className="text-zinc-500">Please log in</p></div>;

  return (
    <div className="min-h-screen bg-zinc-50 pb-20" data-testid="studio-page">
      <GettingStarted pageContext="studio" />
      <div className="bg-gradient-to-b from-zinc-900 to-zinc-800 text-white">
        <div className="max-w-4xl mx-auto px-4 pt-8 pb-10">
          <Link to="/" className="text-zinc-400 hover:text-white mb-4 inline-flex items-center gap-1 text-sm"><ArrowLeft size={16} /> Home</Link>
          <h1 className="font-display font-bold text-3xl md:text-4xl mb-2 flex items-center gap-3"><Sparkles className="text-[#b8ff00]" /> AI Music Studio</h1>
          <p className="text-zinc-400">Generate lyrics, album art, song concepts, and funding strategies — all powered by AI.</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 -mt-4">
        <Tabs defaultValue="lyrics" onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-zinc-200 rounded-xl p-1 mb-6 shadow-sm">
            <TabsTrigger value="lyrics" className="rounded-lg data-[state=active]:bg-zinc-900 data-[state=active]:text-white text-zinc-500" data-testid="tab-lyrics"><Music size={14} className="mr-1" /> Lyrics</TabsTrigger>
            <TabsTrigger value="concept" className="rounded-lg data-[state=active]:bg-zinc-900 data-[state=active]:text-white text-zinc-500" data-testid="tab-concept"><Lightbulb size={14} className="mr-1" /> Concept</TabsTrigger>
            <TabsTrigger value="album_art" className="rounded-lg data-[state=active]:bg-zinc-900 data-[state=active]:text-white text-zinc-500" data-testid="tab-art"><Palette size={14} className="mr-1" /> Album Art</TabsTrigger>
            <TabsTrigger value="funnel" className="rounded-lg data-[state=active]:bg-zinc-900 data-[state=active]:text-white text-zinc-500" data-testid="tab-funnel"><Rocket size={14} className="mr-1" /> Funding Funnel</TabsTrigger>
          </TabsList>

          {/* Lyrics / Concept shared form */}
          {(activeTab === 'lyrics' || activeTab === 'concept') && (
            <TabsContent value={activeTab} forceMount={activeTab === 'lyrics' || activeTab === 'concept'}>
              <div className="bg-white rounded-2xl border border-zinc-200 p-6 space-y-4 shadow-sm">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-zinc-500 mb-1 block">Genre</label>
                    <select value={genre} onChange={e => setGenre(e.target.value)} className="w-full bg-white border border-zinc-200 text-zinc-900 rounded-lg px-3 py-2 text-sm" data-testid="genre-select">
                      {GENRES.map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-zinc-500 mb-1 block">Mood</label>
                    <select value={mood} onChange={e => setMood(e.target.value)} className="w-full bg-white border border-zinc-200 text-zinc-900 rounded-lg px-3 py-2 text-sm" data-testid="mood-select">
                      {MOODS.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">Topic (optional)</label>
                  <Input value={topic} onChange={e => setTopic(e.target.value)} placeholder="Late nights in the city, heartbreak, ambition..." className="border-zinc-200" data-testid="topic-input" />
                </div>
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">Style Reference (optional)</label>
                  <Input value={styleRef} onChange={e => setStyleRef(e.target.value)} placeholder="Like Drake meets Frank Ocean" className="border-zinc-200" data-testid="style-input" />
                </div>
                <Button onClick={() => handleGenerate(activeTab)} disabled={loading} className="w-full bg-zinc-900 text-white font-bold h-12 hover:bg-zinc-800" data-testid="generate-btn">
                  {loading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Generating...</> : <><Sparkles size={16} className="mr-2" /> Generate {activeTab === 'lyrics' ? 'Lyrics' : 'Full Concept'}</>}
                </Button>
              </div>
            </TabsContent>
          )}

          {/* Album Art */}
          <TabsContent value="album_art">
            <div className="bg-white rounded-2xl border border-zinc-200 p-6 space-y-4 shadow-sm">
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Album/Song Title</label>
                <Input value={artTitle} onChange={e => setArtTitle(e.target.value)} placeholder="Midnight Sessions EP" className="border-zinc-200" data-testid="art-title-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">Genre</label>
                  <select value={genre} onChange={e => setGenre(e.target.value)} className="w-full bg-white border border-zinc-200 text-zinc-900 rounded-lg px-3 py-2 text-sm">{GENRES.map(g => <option key={g} value={g}>{g}</option>)}</select>
                </div>
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">Mood</label>
                  <select value={mood} onChange={e => setMood(e.target.value)} className="w-full bg-white border border-zinc-200 text-zinc-900 rounded-lg px-3 py-2 text-sm">{MOODS.map(m => <option key={m} value={m}>{m}</option>)}</select>
                </div>
              </div>
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Style Notes (optional)</label>
                <Input value={artStyle} onChange={e => setArtStyle(e.target.value)} placeholder="Minimalist, neon accents, abstract" className="border-zinc-200" />
              </div>
              <Button onClick={() => handleGenerate('album_art')} disabled={loading} className="w-full bg-zinc-900 text-white font-bold h-12 hover:bg-zinc-800" data-testid="generate-art-btn">
                {loading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Creating artwork...</> : <><Palette size={16} className="mr-2" /> Generate Album Art</>}
              </Button>
            </div>
          </TabsContent>

          {/* Funding Funnel */}
          <TabsContent value="funnel">
            <div className="bg-white rounded-2xl border border-zinc-200 p-6 space-y-4 shadow-sm">
              <p className="text-sm text-zinc-500">Describe your content and AI will generate a complete funding campaign strategy — tiers, pricing, marketing copy, and investor pitch.</p>
              <Input value={funnelTitle} onChange={e => setFunnelTitle(e.target.value)} placeholder="My new EP, album, or project..." className="border-zinc-200" data-testid="funnel-title-input" />
              <Textarea value={funnelDesc} onChange={e => setFunnelDesc(e.target.value)} placeholder="Tell us about your project — what makes it special? Who is it for?" rows={4} className="border-zinc-200" data-testid="funnel-desc-input" />
              <Button onClick={handleFunnel} disabled={funnelLoading} className="w-full bg-[#00aa55] text-white font-bold h-12 hover:bg-[#008844]" data-testid="generate-funnel-btn">
                {funnelLoading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Building strategy...</> : <><Rocket size={16} className="mr-2" /> Generate Funding Strategy</>}
              </Button>
            </div>
          </TabsContent>
        </Tabs>

        {/* Result Display */}
        {result && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-6 bg-white rounded-2xl border border-zinc-200 p-6 shadow-sm" data-testid="studio-result">
            <h3 className="font-bold text-zinc-900 mb-3">Generated Result</h3>
            {result.type === 'album_art' && result.image_url && <img src={result.image_url} alt="Album Art" className="w-64 h-64 rounded-2xl object-cover mb-4 shadow-lg" />}
            {result.content && <pre className="text-sm text-zinc-700 whitespace-pre-wrap font-sans leading-relaxed bg-zinc-50 p-4 rounded-xl">{result.content}</pre>}
          </motion.div>
        )}

        {funnelResult && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-6 bg-white rounded-2xl border border-[#00aa55]/20 p-6 shadow-sm" data-testid="funnel-result">
            <h3 className="font-bold text-zinc-900 mb-3 flex items-center gap-2"><Rocket size={16} className="text-[#00aa55]" /> Your Funding Strategy</h3>
            {parsedFunnel && (
              <div className="mb-4 grid gap-3 md:grid-cols-3">
                <div className="rounded-xl bg-zinc-50 p-4">
                  <p className="text-xs text-zinc-400">Project title</p>
                  <p className="mt-1 font-semibold text-zinc-900">{parsedFunnel.project_title}</p>
                </div>
                <div className="rounded-xl bg-zinc-50 p-4">
                  <p className="text-xs text-zinc-400">Funding goal</p>
                  <p className="mt-1 font-semibold text-zinc-900">${Number(parsedFunnel.funding_goal || 0).toLocaleString()}</p>
                </div>
                <div className="rounded-xl bg-zinc-50 p-4">
                  <p className="text-xs text-zinc-400">Event launch</p>
                  <p className="mt-1 font-semibold text-zinc-900">{parsedFunnel.event_plan?.venue || parsedFunnel.event_plan?.location || 'Bundle draft ready'}</p>
                </div>
              </div>
            )}
            <pre className="text-sm text-zinc-700 whitespace-pre-wrap font-sans leading-relaxed bg-zinc-50 p-4 rounded-xl">{funnelResult.strategy}</pre>
            <div className="mt-4 flex flex-wrap gap-3">
              <Link to="/projects/create" state={launchBundlePrefill ? { prefill: launchBundlePrefill } : undefined}>
                <Button className="bg-[#00aa55] text-white font-bold rounded-full" data-testid="launch-bundle-from-funnel-btn"><Rocket size={14} className="mr-1" /> Open Launch Bundle Draft</Button>
              </Link>
              <Link to="/projects/create">
                <Button variant="outline" className="rounded-full border-zinc-300 text-zinc-700 hover:bg-zinc-50" data-testid="launch-blank-project-from-funnel-btn">Start from blank</Button>
              </Link>
            </div>
          </motion.div>
        )}

        {/* My Items History */}
        {myItems.length > 0 && (
          <div className="mt-8">
            <h3 className="font-bold text-zinc-900 mb-4">Your Studio History</h3>
            <div className="space-y-3">
              {myItems.slice(0, 10).map(item => (
                <div key={item.id} className="p-4 bg-white rounded-xl border border-zinc-200">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${item.type === 'lyrics' ? 'bg-violet-100 text-violet-600' : item.type === 'album_art' ? 'bg-pink-100 text-pink-600' : 'bg-blue-100 text-blue-600'}`}>{item.type}</span>
                    <span className="text-xs text-zinc-400">{item.genre} / {item.mood}</span>
                    <span className="text-xs text-zinc-300 ml-auto">{new Date(item.created_at).toLocaleDateString()}</span>
                  </div>
                  {item.image_url && <img src={item.image_url} alt="" className="w-20 h-20 rounded-lg object-cover" />}
                  {item.content && <p className="text-sm text-zinc-500 line-clamp-3">{item.content}</p>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
